import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * Storage keys with versioning for schema migration support
 */
export enum StorageKeys {
  SURVEY_DATA = 'app:survey:v1',
  ANALYSIS_DATA = 'app:analysis:v1',
  SETTINGS_CACHE = 'app:settings:v1',
}

/**
 * Storage type selection
 */
export type StorageType = 'session' | 'local';

/**
 * Options for saving data
 */
export interface SaveOptions {
  /** Time-to-live in milliseconds. If not set, data never expires */
  ttl?: number;
  /** Storage type: 'session' for sessionStorage, 'local' for localStorage */
  storage?: StorageType;
}

/**
 * Wrapper structure for stored data with metadata
 */
interface StoredData<T> {
  version: number;
  timestamp: number;
  expiresAt: number | null;
  data: T;
}

const CURRENT_VERSION = 1;

/**
 * Centralized persistence manager for storage operations
 * - Supports both sessionStorage and localStorage
 * - TTL-based auto-expiration
 * - Schema versioning for future migrations
 */
@Injectable({ providedIn: 'root' })
export class PersistenceManager {
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Check if running in browser environment
   */
  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Get the appropriate storage based on type
   */
  private getStorage(type: StorageType): Storage | null {
    if (!this.isBrowser) return null;
    return type === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Save data to storage with optional TTL
   * @param key Storage key from StorageKeys enum
   * @param data Data to store
   * @param options Save options (ttl, storage type)
   */
  save<T>(key: StorageKeys, data: T, options: SaveOptions = {}): boolean {
    const storage = this.getStorage(options.storage ?? 'session');
    if (!storage) return false;

    const storedData: StoredData<T> = {
      version: CURRENT_VERSION,
      timestamp: Date.now(),
      expiresAt: options.ttl ? Date.now() + options.ttl : null,
      data,
    };

    try {
      storage.setItem(key, JSON.stringify(storedData));
      return true;
    } catch (e) {
      console.warn(`[PersistenceManager] Failed to save ${key}:`, e);
      return false;
    }
  }

  /**
   * Load data from storage
   * Returns null if data doesn't exist, is expired, or is invalid
   * @param key Storage key from StorageKeys enum
   * @param options Options with storage type
   */
  load<T>(key: StorageKeys, options: { storage?: StorageType } = {}): T | null {
    const storage = this.getStorage(options.storage ?? 'session');
    if (!storage) return null;

    try {
      const raw = storage.getItem(key);
      if (!raw) return null;

      const storedData: StoredData<T> = JSON.parse(raw);

      // Check expiration
      if (storedData.expiresAt && Date.now() > storedData.expiresAt) {
        this.clear(key, options);
        return null;
      }

      // Version check (for future migrations)
      if (storedData.version !== CURRENT_VERSION) {
        // For now, just clear incompatible versions
        // In the future, add migration logic here
        this.clear(key, options);
        return null;
      }

      return storedData.data;
    } catch (e) {
      console.warn(`[PersistenceManager] Failed to load ${key}:`, e);
      return null;
    }
  }

  /**
   * Clear data from storage
   * @param key Storage key from StorageKeys enum
   * @param options Options with storage type
   */
  clear(key: StorageKeys, options: { storage?: StorageType } = {}): boolean {
    const storage = this.getStorage(options.storage ?? 'session');
    if (!storage) return false;

    try {
      storage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`[PersistenceManager] Failed to clear ${key}:`, e);
      return false;
    }
  }

  /**
   * Check if data exists and is not expired
   * @param key Storage key from StorageKeys enum
   * @param options Options with storage type
   */
  exists(key: StorageKeys, options: { storage?: StorageType } = {}): boolean {
    return this.load(key, options) !== null;
  }

  /**
   * Get metadata about stored data (timestamp, expiration)
   * @param key Storage key from StorageKeys enum
   * @param options Options with storage type
   */
  getMetadata(
    key: StorageKeys,
    options: { storage?: StorageType } = {}
  ): { timestamp: number; expiresAt: number | null } | null {
    const storage = this.getStorage(options.storage ?? 'session');
    if (!storage) return null;

    try {
      const raw = storage.getItem(key);
      if (!raw) return null;

      const storedData: StoredData<unknown> = JSON.parse(raw);
      return {
        timestamp: storedData.timestamp,
        expiresAt: storedData.expiresAt,
      };
    } catch {
      return null;
    }
  }

  /**
   * Clear all app-related storage keys
   */
  clearAll(): void {
    Object.values(StorageKeys).forEach(key => {
      this.clear(key, { storage: 'session' });
      this.clear(key, { storage: 'local' });
    });
  }
}
